import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PushPin as PinIcon,
} from '@mui/icons-material';
import { supabase } from '../services/supabase';
import { format } from 'date-fns';


// 공지 등록 시 전체 사용자에게 푸시를 발송한다 (Supabase Edge Function).
// 제목/본문은 알림바 길이에 맞춰 잘라 보낸다.
async function notifyNotice(title, content) {
  try {
    const { data, error } = await supabase.functions.invoke('send-broadcast-notification', {
      body: {
        title: `📢 ${String(title).slice(0, 50)}`,
        body: String(content).replace(/\s+/g, ' ').slice(0, 120),
        type: 'notice',
        mode: 'topic',
      },
    });
    if (error) {
      console.error('공지 푸시 발송 실패:', error);
      return { ok: false, error: error.message || String(error) };
    }
    if (data && data.success === false) {
      return { ok: false, error: data.error || '알 수 없는 오류' };
    }
    return { ok: true, error: null };
  } catch (err) {
    console.error('공지 푸시 호출 실패:', err);
    return { ok: false, error: String(err) };
  }
}

function Notices() {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  // 신규 공지 등록 시 전체 푸시를 함께 보낼지 여부 (수정 시에는 노출하지 않는다)
  const [sendPush, setSendPush] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    is_pinned: false,
  });

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      const { data, error } = await supabase
        .from('notices')
        .select('*')
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      setNotices(data || []);
    } catch (error) {
      console.error('Failed to fetch notices:', error);
      setError('공지사항 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (notice = null) => {
    if (notice) {
      setSelectedNotice(notice);
      setFormData({
        title: notice.title,
        content: notice.content,
        image_url: notice.image_url || '',
        is_pinned: notice.is_pinned,
      });
    } else {
      setSelectedNotice(null);
      setFormData({
        title: '',
        content: '',
        image_url: '',
        is_pinned: false,
      });
    }
    setDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSendPush(false);
    setSelectedNotice(null);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) {
      setError('제목과 내용을 입력하세요.');
      return;
    }

    try {
      const noticeData = {
        title: formData.title,
        content: formData.content,
        image_url: formData.image_url || null,
        is_pinned: formData.is_pinned,
      };

      if (selectedNotice) {
        // Update
        const { error } = await supabase
          .from('notices')
          .update(noticeData)
          .eq('id', selectedNotice.id);

        if (error) throw error;
        setSuccess('공지사항이 수정되었습니다.');
      } else {
        // Create
        const { error } = await supabase
          .from('notices')
          .insert(noticeData);

        if (error) throw error;

        if (sendPush) {
          const push = await notifyNotice(formData.title, formData.content);
          if (push.ok) {
            setSuccess('공지사항이 등록되었고 전체 푸시를 발송했습니다.');
          } else {
            setSuccess('공지사항이 등록되었습니다. (푸시 발송은 실패)');
            setError(`푸시 발송 실패 — ${push.error}`);
          }
        } else {
          setSuccess('공지사항이 등록되었습니다.');
        }
      }

      handleCloseDialog();
      fetchNotices();
    } catch (error) {
      console.error('Failed to save notice:', error);
      setError('공지사항 저장에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('notices')
        .delete()
        .eq('id', selectedNotice.id);

      if (error) throw error;

      setSuccess('공지사항이 삭제되었습니다.');
      setDeleteDialogOpen(false);
      setSelectedNotice(null);
      fetchNotices();
    } catch (error) {
      console.error('Failed to delete notice:', error);
      setError('공지사항 삭제에 실패했습니다.');
    }
  };

  const handleTogglePin = async (notice) => {
    try {
      const { error } = await supabase
        .from('notices')
        .update({ is_pinned: !notice.is_pinned })
        .eq('id', notice.id);

      if (error) throw error;

      setSuccess(notice.is_pinned ? '고정이 해제되었습니다.' : '공지사항이 고정되었습니다.');
      fetchNotices();
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      setError('고정 상태 변경에 실패했습니다.');
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 80 },
    {
      field: 'is_pinned',
      headerName: '고정',
      width: 80,
      renderCell: (params) => (
        <IconButton
          size="small"
          color={params.value ? 'primary' : 'default'}
          onClick={() => handleTogglePin(params.row)}
        >
          <PinIcon />
        </IconButton>
      ),
    },
    { field: 'title', headerName: '제목', flex: 1, minWidth: 200 },
    {
      field: 'content',
      headerName: '내용',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: '등록일',
      width: 160,
      valueFormatter: (value) =>
        value ? format(new Date(value), 'yyyy-MM-dd HH:mm') : '',
    },
    {
      field: 'actions',
      headerName: '작업',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleOpenDialog(params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => {
              setSelectedNotice(params.row);
              setDeleteDialogOpen(true);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">공지사항 관리</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          새 공지사항
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={notices}
          columns={columns}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          loading={loading}
          disableRowSelectionOnClick
          columnVisibilityModel={{ id: false }}
        />
      </Box>

      {/* 공지사항 생성/수정 다이얼로그 */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedNotice ? '공지사항 수정' : '새 공지사항'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="제목"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="내용"
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            margin="normal"
            multiline
            rows={8}
            required
          />
          <TextField
            fullWidth
            label="이미지 URL"
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            margin="normal"
            placeholder="https://example.com/image.jpg"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_pinned}
                onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
              />
            }
            label="상단 고정"
            sx={{ mt: 2 }}
          />
          {!selectedNotice && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={sendPush}
                    onChange={(e) => setSendPush(e.target.checked)}
                  />
                }
                label="등록과 함께 전체 푸시 발송"
                sx={{ mt: 1, display: 'block' }}
              />
              {sendPush && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  모든 사용자에게 즉시 발송되며 취소할 수 없습니다.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {selectedNotice ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>공지사항 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            "{selectedNotice?.title}" 공지사항을 삭제하시겠습니까?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Notices;
